import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { useToast } from "@chakra-ui/react";
import { apiEmailTemplateById } from "helpers/url/api/v2";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { EmailTemplate } from "./models";

import { sharedTemplateKeys } from "../SharedTemplate/hooks";
import { toCreateEmailTemplatePayload, toUpdateEmailTemplatePayload } from "./helpers";
import { EmailTemplateSchema } from "./schema";

export const emailTemplateKeys = {
  all: ["emailTemplates"] as const,
  lists: () => [...emailTemplateKeys.all, "list"] as const,
  list: (filters: string) => [...emailTemplateKeys.lists(), { filters }] as const,
  details: () => [...emailTemplateKeys.all, "detail"] as const,
  detail: (id: string) => [...emailTemplateKeys.details(), id] as const,
};

export function setNewEmailTemplateInQueryClient(queryClient: QueryClient, newEmailTemplate: EmailTemplate) {
  queryClient.setQueryData(emailTemplateKeys.detail(newEmailTemplate.id), (oldData: EmailTemplate | undefined) => {
    if (!oldData) {
      return newEmailTemplate;
    }
    return {
      ...oldData,
      ...newEmailTemplate,
    };
  });
}

export const useEmailTemplateDetailQuery = (id: string | undefined, options = {}) => useQuery<EmailTemplate | null>({
  queryKey: emailTemplateKeys.detail(id as string),
  queryFn: async () => apiClient.get(apiEmailTemplateById(id as string)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
  ...options,
});

export const useEmailTemplateListQuery = (queryString: string, options = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<EmailTemplate[]>({
    queryKey: queryString ? emailTemplateKeys.list(queryString) : emailTemplateKeys.lists(),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.emailTemplates.base)}${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
};

function useEmailTemplateCreateRequest(mutationFn: (data: EmailTemplateSchema) => Promise<EmailTemplate>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      setNewEmailTemplateInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

function useEmailTemplateUpdateRequest(mutationFn: (data: Partial<EmailTemplateSchema>) => Promise<EmailTemplate>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      setNewEmailTemplateInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useUpdateEmailTemplate(emailTemplateId: string) {
  return useEmailTemplateUpdateRequest(
    async (formData: Partial<EmailTemplateSchema>): Promise<EmailTemplate> => {
      const payload = toUpdateEmailTemplatePayload(formData);
      return apiClient.put<EmailTemplate>(apiEmailTemplateById(emailTemplateId), { emailTemplate: payload });
    },
  );
}

export function useCreateEmailTemplate() {
  return useEmailTemplateCreateRequest(
    async (formData: EmailTemplateSchema): Promise<EmailTemplate> => {
      const payload = toCreateEmailTemplatePayload(formData);
      return apiClient.post<EmailTemplate>(API_ROUTES.emailTemplates.base, { emailTemplate: payload });
    },
  );
}
